import * as React from 'react';
import * as Classes from '../common/classes';
import { Icon } from '../icon/Icon';
import { Menu, MenuContext } from './Menu';
import classNames from 'classnames';
import { mcNavigateNext } from '@mc/react-ui-icons';
import type { MenuItemIntent } from '../common/props';
import { Popup, PopupProps } from '../popover/Popup';
import { useContext } from 'react';

interface Props extends Omit<React.HTMLAttributes<HTMLLIElement>, 'label'> {
  /**
   * Text položky
   */
  text: React.ReactNode;
  /**
   * Typ informace.
   */
  intent?: MenuItemIntent;
  icon?: React.ReactNode;
  /**
   * Zda by se tato položka měla zobrazit jako aktivní.
   */
  active?: boolean;
  /**
   * Zda je item interaktivní.
   */
  disabled?: boolean;
  popoverProps?: Partial<PopupProps>;
  /**
   * Název tagu, který obaluje obsah MenuItemu.
   *
   * @default a
   */
  tagName?: keyof React.JSX.IntrinsicElements;
  /**
   * Classa, která se předá textu itemu.
   */
  textClassName?: string;
  /**
   * HTML titulek.
   */
  htmlTitle?: string;
  children?: React.ReactNode;
}

/**
 * MenuItem - typicky pro dropdown menu.
 */
const MenuItem: React.FC<Props> = React.forwardRef<HTMLLIElement, Props>(
  (
    {
      text,
      intent,
      icon,
      active = false,
      disabled = false,
      popoverProps,
      tagName = 'a',
      textClassName,
      htmlTitle,
      className,
      children,
      ...restProps
    },
    ref
  ) => {
    const menuContext = useContext(MenuContext);

    const hasSubMenu = !!children;
    const liClassName = classNames({
      [Classes.MENU_ITEM_SUBMENU]: hasSubMenu,
    });
    const targetClassName = classNames(
      Classes.MENU_ITEM,
      {
        [Classes.ACTIVE]: active,
        [Classes.MENU_ITEM_DISABLED]: disabled,
        [Classes.TYPOGRAPHY_DANGER]: intent === 'danger',
        [Classes.TYPOGRAPHY_SUCCESS]: intent === 'success',
        [Classes.TYPOGRAPHY_WARNING]: intent === 'warning',
        [Classes.TYPOGRAPHY_DEFAULT]: !intent || intent === 'default',
      },
      className
    );
    const target = React.createElement(
      tagName,
      {
        ...restProps,
        ...(disabled ? DISABLED_PROPS : {}),
        onClick: (e) => {
          // Nemá onclick a má submenu, takže při kliknutí na položku nic nechceme dělat, rozhodně ji nechceme zavřít.
          // @ts-ignore
          if (!disabled) {
            restProps?.onClick?.(
              e as React.MouseEvent<HTMLLIElement, MouseEvent>
            );
          }
          if (disabled || (!restProps.onClick && hasSubMenu)) {
            e.preventDefault();
            e.stopPropagation();
          }
        },
        className: targetClassName,
      },
      <div className={Classes.MENU_ITEM_CONTENT}>
        <div className={Classes.MENU_ITEM_ICON}>{icon}</div>
        <div
          className={classNames(Classes.MENU_ITEM_TEXT, textClassName)}
          title={htmlTitle}
        >
          {text}
        </div>
      </div>,
      hasSubMenu ? (
        <Icon
          className={Classes.MENU_ITEM_CHEVRON}
          icon={mcNavigateNext}
          style={{
            marginLeft: 17,
          }}
        />
      ) : null
    );

    const popupPopoverProps = {
      ...popoverProps,
      nested: popoverProps?.nested ?? true,
    };

    return (
      <li ref={ref} className={liClassName}>
        {!children ? (
          target
        ) : (
          <Popup
            on={'hover'}
            disabled={disabled}
            position={['right top', 'right bottom', 'left top', 'left bottom']}
            {...popupPopoverProps}
            trigger={target}
          >
            {(closePopup: () => void) => {
              return (
                <div
                  onClick={
                    menuContext.closeOnInnerClick
                      ? () => {
                          closePopup();
                          menuContext.closePopup();
                        }
                      : undefined
                  }
                >
                  <Menu>{children}</Menu>
                </div>
              );
            }}
          </Popup>
        )}
      </li>
    );
  }
);

const DISABLED_PROPS: React.AnchorHTMLAttributes<HTMLAnchorElement> = {
  href: undefined,
  onClick: undefined,
  onMouseDown: undefined,
  onMouseEnter: undefined,
  onMouseLeave: undefined,
  tabIndex: -1,
};

export { MenuItem };
